/**
 * <kismet-reviews> — custom reviews carousel powered by Yotpo's
 * Storefront API v3. Replaces Yotpo's widget so we control the
 * DOM 1:1 against the Figma design (node 535-27).
 *
 * Attributes:
 *   data-app-key      Yotpo App Key (the loader GUID in our case).
 *   data-product-id   Shopify product ID (optional — omit for site-wide).
 *   data-per-page     Reviews to fetch (default 20).
 *
 * Yotpo response field names differ between endpoints:
 *   site-wide  → user.display_name, created_at  (snake_case)
 *   per-product → user.displayName, createdAt  (camelCase)
 * The renderer handles both.
 */

(function () {
  if (customElements.get('kismet-reviews')) return;

  function getProductSchemaId() {
    const script = document.querySelector('script[data-kismet-product-schema]');
    if (!script) return null;
    try {
      const data = JSON.parse(script.textContent);
      return data && data['@id'] ? data['@id'] : null;
    } catch (e) {
      return null;
    }
  }

  function injectReviewSchema(reviews) {
    const productId = getProductSchemaId();
    if (!productId) return;
    if (document.querySelector('script[data-kismet-reviews-schema]')) return;
    const reviewEntries = reviews
      .map((r) => {
        const userObj = r.user || {};
        const name = userObj.display_name || userObj.displayName || 'Anonymous';
        const dateRaw = r.created_at || r.createdAt || '';
        // Trim to date portion only — Schema.org's Date type expects ISO
        // 8601 YYYY-MM-DD; Yotpo returns full ISO timestamps.
        const date = dateRaw ? String(dateRaw).slice(0, 10) : '';
        const rawScore = Math.round(r.score || 0);
        const body = r.content || '';
        // Drop entries with no body OR no valid star rating — otherwise
        // we'd clamp 0/missing scores up to 1 and pollute the Product's
        // schema-derived rating distribution with phantom 1-star reviews.
        if (!body || rawScore < 1) return null;
        const score = Math.min(5, rawScore);
        const entry = {
          '@type': 'Review',
          author: { '@type': 'Person', name: name },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: score,
            bestRating: 5,
            worstRating: 1
          },
          reviewBody: body
        };
        if (date) entry.datePublished = date;
        if (r.title) entry.name = r.title;
        return entry;
      })
      .filter(Boolean);
    if (!reviewEntries.length) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-kismet-reviews-schema', '');
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': productId,
      review: reviewEntries
    });
    document.head.appendChild(script);
  }

  // Figma star (14×14, rounded points); colour comes from --kr-star via currentColor.
  const STAR_SVG =
    '<svg viewBox="0 0 14 14" aria-hidden="true"><path fill="currentColor" d="M6.05984 0.439302C6.40326 -0.146461 7.25006 -0.14646 7.59348 0.439303L9.22574 3.22343C9.35106 3.43719 9.55993 3.58894 9.80196 3.64207L12.9542 4.3341C13.6174 4.4797 13.8791 5.28506 13.4281 5.79268L11.2847 8.20539C11.1201 8.39064 11.0403 8.63618 11.0646 8.88278L11.3805 12.0946C11.447 12.7703 10.7619 13.2681 10.1398 12.9961L7.18278 11.7031C6.95574 11.6038 6.69758 11.6038 6.47054 11.7031L3.51354 12.9961C2.89141 13.2681 2.20633 12.7703 2.2728 12.0946L2.58874 8.88278C2.613 8.63618 2.53322 8.39064 2.36865 8.20539L0.225186 5.79268C-0.225786 5.28506 0.0358918 4.4797 0.699107 4.3341L3.85136 3.64207C4.09339 3.58894 4.30226 3.43719 4.42758 3.22343L6.05984 0.439302Z"/></svg>';

  const CHECK_SVG =
    '<svg class="kr-meta__check" viewBox="0 0 12 12" role="img" aria-label="Verified Buyer"><circle cx="6" cy="6" r="6" fill="currentColor"/><path d="M3.4 6.1l1.7 1.7 3.5-3.6" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

  const ARROW_LEFT =
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 2L4 8l6 6" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const ARROW_RIGHT =
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 2l6 6-6 6" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatDate(raw) {
    if (!raw) return '';
    // Accept both "2026-02-02T00:00:00" and "2026-02-02". Read the calendar
    // date straight from the string: it is what the JSON-LD datePublished
    // slices too, so the visible mm/dd/yy always matches the structured
    // data (Google's "structured data must match visible content" policy).
    // Parsing through Date would treat Yotpo's zone-less timestamps as
    // local time and shift evening reviews to the next day.
    const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    return m[2] + '/' + m[3] + '/' + m[1].slice(-2);
  }

  function firstLetter(name) {
    if (!name) return '?';
    const c = String(name).trim().charAt(0).toUpperCase();
    return c || '?';
  }

  function pickUserName(user) {
    if (!user) return '';
    return user.display_name || user.displayName || '';
  }

  function pickCreatedAt(review) {
    return review.created_at || review.createdAt || '';
  }

  function starsHtml(score) {
    let html = '';
    const n = Math.max(0, Math.min(5, Math.round(score || 0)));
    for (let i = 0; i < n; i++) html += STAR_SVG;
    return html;
  }

  function cardHtml(review) {
    const name = pickUserName(review.user) || 'Anonymous';
    const date = formatDate(pickCreatedAt(review));
    // Only show the Verified Buyer badge when Yotpo explicitly says so —
    // null/undefined means unknown (imported, syndicated, or legacy
    // reviews) and must not be advertised as verified (FTC endorsement
    // guidance treats false "verified" claims as misleading).
    const verified =
      review.verified_buyer === true || review.verifiedBuyer === true;
    const title = review.title || '';
    const body = review.content || '';
    const score = review.score || 0;

    return (
      '<article class="kr-card">' +
        '<header class="kr-card__head">' +
          '<span class="kr-avatar" aria-hidden="true">' + escapeHtml(firstLetter(name)) + '</span>' +
          '<div class="kr-meta">' +
            '<span class="kr-meta__name">' +
              '<span class="kr-meta__name-text">' + escapeHtml(name) + '</span>' +
              (verified ? CHECK_SVG : '') +
            '</span>' +
            (date ? '<time class="kr-meta__date">' + escapeHtml(date) + '</time>' : '') +
          '</div>' +
          '<div class="kr-card__stars" role="img" aria-label="' + score + ' out of 5 stars">' +
            starsHtml(score) +
          '</div>' +
        '</header>' +
        (title ? '<h3 class="kr-card__title">' + escapeHtml(title) + '</h3>' : '') +
        (body ? '<p class="kr-card__body">' + escapeHtml(body) + '</p>' : '') +
        (body ? '<button type="button" class="kr-card__more" data-more aria-expanded="false">Read more</button>' : '') +
      '</article>'
    );
  }

  const PAGE_SIZE = 5;   // Yotpo's hard cap per request
  const MAX_BATCHES = 2; // at most 2 × ceil(target / 5) requests per load

  class KismetReviews extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._reviews = [];
      this._cardsPerView = 3;
      this._onResize = this._onResize.bind(this);
      this._resizeObs = null;
    }

    connectedCallback() {
      this.setAttribute('data-state', 'loading');
      this.innerHTML = '<p class="kr-status">Loading reviews…</p>';
      this._fetch()
        .then((reviews) => {
          this._reviews = reviews || [];
          if (!this._reviews.length) {
            this.setAttribute('data-state', 'empty');
            this.innerHTML = '<p class="kr-status">No reviews yet.</p>';
            return;
          }
          injectReviewSchema(this._reviews.slice(0, 10));
          this._render();
          this._onResize();
          // ResizeObserver watches the component's own width — the
          // Shopify editor preview iframe is much narrower than the
          // browser window, so window.innerWidth would mis-classify
          // and pack 3 cards into ~600px (cards became ~200px wide).
          if (typeof ResizeObserver !== 'undefined') {
            this._resizeObs = new ResizeObserver(this._onResize);
            this._resizeObs.observe(this);
          } else {
            window.addEventListener('resize', this._onResize);
          }
        })
        .catch((err) => {
          console.error('[kismet-reviews] fetch failed:', err);
          this.setAttribute('data-state', 'error');
          this.innerHTML = '<p class="kr-status">Couldn’t load reviews right now.</p>';
        });
    }

    disconnectedCallback() {
      if (this._resizeObs) {
        this._resizeObs.disconnect();
        this._resizeObs = null;
      }
      window.removeEventListener('resize', this._onResize);
    }

    // Yotpo's storefront endpoints return at most 5 reviews per page no
    // matter what per_page says, and ignore the `star` filter, so the
    // component pages through newest-first and filters the score itself.
    _endpoint(page) {
      const appKey = this.getAttribute('data-app-key');
      const productId = this.getAttribute('data-product-id');
      const base = 'https://api-cdn.yotpo.com/v3/storefront/store/' + encodeURIComponent(appKey);
      const path = productId
        ? '/product/' + encodeURIComponent(productId) + '/reviews'
        : '/reviews';
      return base + path + '?per_page=' + PAGE_SIZE + '&page=' + page + '&sort=date&direction=desc';
    }

    async _fetchPage(page) {
      const res = await fetch(this._endpoint(page), { credentials: 'omit' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      return Array.isArray(data.reviews) ? data.reviews : [];
    }

    // Newest first, keeping only reviews at or above the minimum score.
    // Pages are requested in parallel batches sized to the target; a second
    // batch only runs when the filter left us short and the first came back
    // full (i.e. there may be more).
    async _fetch() {
      const target = parseInt(this.getAttribute('data-per-page'), 10) || 20;
      const minScore = parseInt(this.getAttribute('data-min-score'), 10) || 0;
      const pagesPerBatch = Math.ceil(target / PAGE_SIZE);
      const keep = (r) => !r.deleted && (r.score || 0) >= minScore;
      let kept = [];
      let page = 1;
      for (let batch = 0; batch < MAX_BATCHES && kept.length < target; batch++) {
        const pages = [];
        for (let i = 0; i < pagesPerBatch; i++) pages.push(this._fetchPage(page++));
        const results = await Promise.all(pages);
        results.forEach((list) => { kept = kept.concat(list.filter(keep)); });
        const lastFull = results[results.length - 1].length === PAGE_SIZE;
        if (!lastFull) break;
      }
      return kept.slice(0, target);
    }

    _render() {
      const cards = this._reviews.map(cardHtml).join('');
      this.innerHTML =
        '<div class="kr-carousel">' +
          '<button type="button" class="kr-arrow kr-arrow--prev" data-prev aria-label="Previous reviews">' + ARROW_LEFT + '</button>' +
          '<div class="kr-viewport">' +
            '<div class="kr-track" data-track>' + cards + '</div>' +
          '</div>' +
          '<button type="button" class="kr-arrow kr-arrow--next" data-next aria-label="Next reviews">' + ARROW_RIGHT + '</button>' +
        '</div>';
      this.setAttribute('data-state', 'ready');

      this._track = this.querySelector('[data-track]');
      this._prev = this.querySelector('[data-prev]');
      this._next = this.querySelector('[data-next]');
      this._prev.addEventListener('click', () => this._step(-1));
      this._next.addEventListener('click', () => this._step(1));

      this._track.addEventListener('click', (event) => {
        const button = event.target.closest('[data-more]');
        if (!button) return;
        const card = button.closest('.kr-card');
        const expanded = card.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? 'Show less' : 'Read more';
      });
    }

    // Show the toggle only on cards whose body is actually clamped. Runs
    // after every layout change since the clamp depends on the card width.
    _markOverflow() {
      if (!this._track) return;
      this._track.querySelectorAll('.kr-card').forEach((card) => {
        if (card.classList.contains('is-expanded')) return;
        const body = card.querySelector('.kr-card__body');
        const clipped = body && body.scrollHeight > body.clientHeight + 1;
        card.toggleAttribute('data-overflow', !!clipped);
      });
    }

    _onResize() {
      // Width of the component itself — NOT window.innerWidth. The
      // Shopify editor preview iframe is much narrower than the
      // browser window; measuring from the host element makes the
      // carousel responsive to whatever container it lives in.
      const w = this.getBoundingClientRect().width;
      let cpv = 3;
      let bp = 'lg';
      let gap = 16;
      if (w <= 520) { cpv = 1; bp = 'sm'; gap = 12; }
      else if (w <= 900) { cpv = 2; bp = 'md'; }
      this._cardsPerView = cpv;
      this.setAttribute('data-bp', bp);
      // Lets the CSS center the row only when every review fits.
      this.toggleAttribute('data-overflow', this._reviews.length > cpv);
      this.style.setProperty('--kr-cards-per-view', String(cpv));
      this.style.setProperty('--kr-gap', gap + 'px');
      this._index = Math.min(this._index, Math.max(0, this._reviews.length - cpv));
      this._update();
      this._markOverflow();
    }

    _step(dir) {
      const max = Math.max(0, this._reviews.length - this._cardsPerView);
      this._index = Math.max(0, Math.min(max, this._index + dir));
      this._update();
    }

    _update() {
      if (!this._track) return;
      const cards = this._track.children;
      if (!cards.length) return;
      // Measure first card + gap to translate exactly N steps.
      const cardWidth = cards[0].getBoundingClientRect().width;
      const styles = getComputedStyle(this._track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const offset = (cardWidth + gap) * this._index;
      this._track.style.transform = 'translateX(' + (-offset) + 'px)';

      const max = Math.max(0, this._reviews.length - this._cardsPerView);
      this._prev.disabled = this._index <= 0;
      this._next.disabled = this._index >= max;
    }
  }

  customElements.define('kismet-reviews', KismetReviews);
})();
